<script>
import { componentStore } from "@common/componentStore/componentStore";
import { library } from "@common/library/library";
import { defineAsyncComponent } from "@common/defineAsyncComponent";
import moment from "moment/moment";

const controlComponent = defineAsyncComponent(() => import('@common/control/control.vue'));

let datasetListConfig = {
    cId: "datasetList",
    cType: "datasetList",
    cTitle: "Dataset List",
    cTemplate: `<div class="dataset-list">
                    <div class="button-area">
                        <div class="new-control">
                            <control name="CREATE_NEW_DATASET"></control>
                        </div>
                        <control name="RELOAD_DATASETS_LIST"></control>
                    </div>
                    <condensed-filter
                        :prop-filter-options="filterOptions"
                        :prop-default-applied-filters="defaultAppliedFilters"
                        cId="condensedFilter__datasets"
                        :prop-filter-items="datasets"
                        :component-store-id="storeId">
                    </condensed-filter>
                    <control v-if="!filtering" name="DATASET_LIST" :items="formattedItems"></control>
                </div>`,
    controls: [
        {
            name: 'RELOAD_DATASETS_LIST',
            type: "button",
            onclick: "reloadDatasets",
            hideLabel: true,
            iconCss: 'e-icons e-reload',
        },
        {
            name: 'DATASET_LIST',
            type: 'list',
            onclick: 'selectDataset',
            itemContentTemplate: `<div class="sub-list-item-container" :class="{'selected-dataset':component.selectedDataset===data.dsId}" :id="'datasetlist-dataset-item-' + data.ID" @mouseenter="onMouseenter()" @mouseleave="onMouseleave()">
                            <div class="dataset-header">
                                <div class="dataset-label">
                                    <span class="dataset-title" :title="data.parsedTitle">{{data.parsedTitle}}<span v-if="data.isNew" class="dataset-status-flag">NEW</span></span>
                                    <div class="component-list-more-details">
                                        {{data.gridType}}
                                        <span class="detail-indicator dataset-lock" title="Dataset Lock Status">
                                            <ejs-tooltip :content="data.locked ? 'Locked' : 'Unlocked'">
                                                <span v-if="data.locked">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-lock" viewBox="0 0 16 16">
                                                        <path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2zm3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2zM5 8h6a1 1 0 0 1 1 1v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z"/>
                                                    </svg>
                                                </span>
                                                <span v-else>
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-unlock" viewBox="0 0 16 16">
                                                        <path d="M11 1a2 2 0 0 0-2 2v4a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h5V3a3 3 0 0 1 6 0v4a.5.5 0 0 1-1 0V3a2 2 0 0 0-2-2zM3 8a1 1 0 0 0-1 1v5a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V9a1 1 0 0 0-1-1H3z"/>
                                                    </svg>
                                                </span>
                                            </ejs-tooltip>
                                        </span>
                                        <span class="detail-indicator" v-if="data.isUnused">
                                            <ejs-tooltip content="This dataset hasn't been used for 180 days or more">UN</ejs-tooltip>
                                        </span>
                                        <span class="detail-indicator" v-if="data.isModified === 2"><ejs-tooltip content="Instance Specific">LCL</ejs-tooltip></span>
                                        <span class="detail-indicator" v-if="data.isModified === 1"><ejs-tooltip content="Modified">MDF</ejs-tooltip></span>
                                        <span class="detail-indicator" v-if="data.dateLastModified">
                                            <ejs-tooltip :content="'Modified: ' + data.dateLastModified + ' (' + data.daysSinceLastModify + ' days ago)'">M</ejs-tooltip>
                                        </span>
                                        <span class="detail-indicator" v-if="data.instances && data.instances.length"><ejs-tooltip :content="'Instances: ' + data.instances.join(', ')">I</ejs-tooltip></span>
                                        <span class="detail-indicator" v-if="data.components && data.components.length"><ejs-tooltip :content="'Components: ' + data.components.join(', ')">C</ejs-tooltip></span>
                                        <span class="detail-indicator" v-if="data.outOfDate">
                                            <ejs-tooltip :content="'Out of sync: ' + data.outOfDate + ' day(s)'">O {{data.outOfDate}}</ejs-tooltip>
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div class="action-area">
                                <control name="DATASET_ACTIONS" :items="data.items"></control>
                            </div>
                        </div>`
        },
        {
            name: 'DATASET_ACTIONS',
            type: 'dropdownbutton',
            initOnRender: true,
            onselect: 'datasetActions',
            cssClass: 'e-block preview-link'
        },
        {
            name: 'CREATE_NEW_DATASET',
            type: "dropdownbutton",
            label: "Create new dataset",
            onclick: "newDataset",
            onselect: "newDataset",
            cssClass: "e-block e-info",
            items: [
                { value: 1, text: 'Volunteer dataset' },
                { value: 51, text: 'Volunteer logbook dataset' },
                { value: 2, text: 'Opportunity dataset' },
                { value: 52, text: 'Opportunity logbook dataset' },
                { value: 3, text: 'Organization dataset' },
                { value: 53, text: 'Organization logbook dataset' },
                { value: 5, text: 'Client dataset' },
                { value: 55, text: 'Client logbook dataset' },
                { value: 11, text: 'Calendar dataset' },
                { value: 32, text: 'Affiliations dataset' },
                { value: 14, text: 'Professions dataset' },
            ]
        },
    ],
    layoutDatamap: [
        {
            "property": "selectedDataset", "from": "datasetList", "to": "thisStore", "as": "selectedDataset"
        },
    ]
};

const datasetListCmp = {
    name: 'datasetList',
    globalMethods: ['getDatasets'],
    data() {
        return {
            datasets: {},
            selectedDataset: {},
            editedDataset: {},
            filteredItems: {},
            filtering: false,
            autoUpdateListInterval: false,
            defaultAppliedFilters: {
                isUnused: ['false']
            }
        };
    },
    watch: {
        'filteredItems': function (prevValue, newValue) {
            if (!library.isEqualObj(prevValue, newValue)) {
                this.filtering = true;
                this.$nextTick(() => {
                    this.filtering = false;
                });
            }
        },
    },
    computed: {
        filterOptions: function () {
            return {
                cTitle: { label: 'Dataset Title' },
                pspName: { label: 'Perspective' },
                isUnused: {
                    label: 'Show Unused',
                    required: true,
                    disabled: true,
                    type: 'dropdown',
                    maximumSelectionLength: 1,
                    filterType: 'Contains',
                    showFilterLinksInSummary: true,
                    exactMatch: false,
                    values: [
                        {
                            text: "All", value: 'all', linkText: 'all', switchTo: 'false'
                        },
                        {
                            text: "Only Used", value: 'false', linkText: 'used', switchTo: 'true'
                        },
                        {
                            text: "Only Unused", value: 'true', linkText: 'unused', switchTo: 'all'
                        },
                    ]
                },
                instances: {
                    label: "Instance",
                    type: "dropdown",
                    required: true,
                    disabled: true
                },
                components: {
                    label: "Component",
                    type: "dropdown",
                    required: true,
                    disabled: true
                },
                dateLastModifyUnixDay: {
                    label: 'Last Modified',
                    required: true,
                    type: 'date'
                },
            };
        },
        formattedItems() {
            return this.filteredItems;
        }
    },
    methods: {
        datasetActions(extraCommand) {
            if (extraCommand.item && extraCommand.item.dsId) {
                switch (extraCommand.item.value) {
                    case 'edit':
                        this.editDataset(extraCommand.item.dsId);
                        break;
                    case 'lockUnlock':
                        this.lockUnlockDataset(extraCommand.item.dsId);
                        break;
                    case 'copy':
                        this.copyDataset(extraCommand.item.dsId);
                        break;
                    case 'delete':
                        this.deleteDataset(extraCommand.item.dsId);
                        break;
                    case 'reset':
                        this.resetDataset(extraCommand.item.dsId);
                        break;
                    case 'copyDatasetToDevDash':
                        this.copyDatasetToDevDash(extraCommand.item.dsId);
                        break;
                    case 'copyDatasetToDevDashQA':
                        this.copyDatasetToDevDashQA(extraCommand.item.dsId);
                        break;
                    default:
                        break;
                }
            } else {
                this.selectDataset(extraCommand);
            }
        },
        async getDatasets(selectedDatasetId, refresh) {
            let datasets = await configStore.getDatasetList(refresh);
            for (let i in datasets) {
                if (i === 'length') continue;
                let lockUnlockLabel = datasets[i].locked ? 'Unlock Dataset' : 'Lock Dataset';
                let isModified = parseInt(datasets[i].isModified, 10);
                let items = [];
                if (!datasets[i].locked) {
                    items.push({ text: 'Edit Dataset', value: 'edit' });
                    items.push({ text: 'Delete Dataset', value: 'delete' });
                }
                items.push({ text: lockUnlockLabel, value: 'lockUnlock' });
                items.push({ text: 'Copy Dataset', value: 'copy' });
                if (!datasets[i].locked && isModified === 1) {
                    items.push({ text: 'Discard changes', value: 'reset' });
                }
                if (typeof IS_DEVDASH_QA !== 'undefined') {
                    if (IS_DEVDASH_QA) {
                        items.push({ text: 'Copy to DevDash', value: 'copyDatasetToDevDash' });
                    } else {
                        items.push({ text: 'Copy to DevDashQA', value: 'copyDatasetToDevDashQA' });
                    }
                }

                for (let j in items) {
                    if (j === 'length') continue;
                    items[j].dsId = datasets[i].dsId;
                }
                datasets[i].items = Object.values(items);
                datasets[i].pspName = datasets[i].isLogBook
                    ? library.getPspName('10')
                    : library.getPspName(datasets[i].pspID);
                let tmpTitle = datasets[i].cTitle.split(':');
                let gridType = tmpTitle.shift();
                datasets[i].parsedTitle = tmpTitle.join();
                datasets[i].gridType = gridType;
                let today = new Date();
                let lastModify = datasets[i].dateLastModified || today;
                datasets[i].instances = Array.isArray(datasets[i].instances) ? datasets[i].instances : [];
                datasets[i].components = Array.isArray(datasets[i].components) ? datasets[i].components : [];
                datasets[i].dateLastModifyUnixDay = moment(lastModify)
                    .startOf('day').format('x');
                datasets[i].dateLastModified = moment(lastModify).format('MM-DD-YYYY hh:mma');
            }
            this.datasets = datasets;
            if (selectedDatasetId) {
                let dataset = datasets.find((iDataset) => iDataset.ID === selectedDatasetId || iDataset.dsId === selectedDatasetId);
                if (dataset) {
                    this.selectedDataset = false;
                    this.$nextTick(() => {
                        this.selectDataset(dataset);
                    });
                }
            }
        },
        onLoad() {
            this.getDatasets();
            // Add listener for reloadDatasetList
            window.addEventListener("message", (event) => {
                if (typeof event.data === 'string' && event.data.indexOf('reloadDatasetList') !== -1) {
                    let message = event.data.split(':');
                    let newDatasetId = message.length === 2 ? message[1] : false; // NOSONAR
                    if (!newDatasetId) newDatasetId = this.selectedDataset;
                    this.selectedDataset = false;
                    if (newDatasetId) {
                        this.$nextTick(() => {
                            this.getDatasets(newDatasetId, true);
                        });
                    }
                }
            });

            this.autoUpdateListInterval = setInterval(async () => {
                await this.reloadDatasets();
            }, 5 * 60 * 1000);
        },
        async reloadDatasets() {
            await this.getDatasets(false, true);
        },
        async newDataset(clickParams) {
            if (clickParams && clickParams.item && clickParams.item.value) {
                let passedParams = {
                    psp: clickParams.item.value
                };
                await actionStore.createDataset(passedParams);
            }
        },
        selectDataset(dataset) {
            let dsId = dataset.dsId;
            if (!dsId && dataset.ID) {
                for (let i in this.datasets) {
                    if (i === 'length') continue;
                    if (this.datasets[i].ID === parseInt(dataset.ID, 10)) {
                        dsId = this.datasets[i].dsId;
                    }
                }
            }
            if (dsId) {
                this.selectedDataset = dsId;
                setTimeout(() => {
                    let elem = document.getElementById('datasetlist-dataset-item-' + dataset.ID);
                    if (elem) elem.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 1000); // NOSONAR
            }
        },
        async editDataset(dsId) {
            this.editedDataset = dsId;
            this.sDs.post("sds_propogateDatasetFromGlobalToLocal", dsId).then(async () => {
                let instance = await configStore.getDevDashLocalInstanceDetails();
                let tmpUrl = instance.url + '/coordinator/index.php?editLocalDataset&datasetID=' + encodeURIComponent(dsId) + (instance.sId ? '&copyPHPSESSID=' + instance.sId : '');
                let tmpWindow = window.open(tmpUrl, 'editedDatasetArea', 'scrollbars=yes,resizable=yes,width=1000,height=1000,status=yes,top=100,left=100');
                this.windowCheckAndReload(tmpWindow);
            });
        },
        async lockUnlockDataset(dsId) {
            this.editedDataset = dsId;
            if (Array.isArray(this.datasets)) {
                let tmpDataset = this.datasets.find((tmp) => tmp.dsId === dsId);
                if (tmpDataset) {
                    if (tmpDataset.locked) {
                        let secretValue = '';
                        let lockTemplate = {
                            name: "lock-template",
                            template: `<div><control name="SECRET" :prop-config="secretConfig" :value="secretValue" @update:value="secretValue = $event"></control></div>`,
                            data() {
                                return {
                                    secretValue: secretValue,
                                    secretConfig: {
                                        name: 'SECRET',
                                        type: 'string',
                                        label: 'Please enter the secret key to unlock',
                                        helpText: 'Please type UNLOCK in all caps.'
                                    }
                                };
                            },
                            watch: {
                                'secretValue': function (newVal) {
                                    secretValue = newVal;
                                }
                            },
                            components: {
                                control: controlComponent
                            }
                        };
                        library.showDialog(lockTemplate, async () => {
                            if (secretValue === 'UNLOCK') {
                                await this.sDs.post('sds_setDatasetLocked', dsId, !tmpDataset.locked);
                                this.getDatasets(dsId, true);
                            }
                        }, { title: 'Unlock Dataset' });
                    } else {
                        await this.sDs.post('sds_setDatasetLocked', dsId, !tmpDataset.locked);
                        this.getDatasets(dsId, true);
                    }
                }
            }
        },
        windowCheckAndReload(tmpWindow) {
            let tmpInterval = setInterval(() => {
                if (tmpWindow.closed) {
                    clearInterval(tmpInterval);
                    this.reloadDatasets();
                }
            }, 500); // NOSONAR
        },
        async copyDataset(dsId, instance) {
            let newDatasetName = '';
            let funcPostSuccess = () => {
                this.sDs.post("sds_copyDataset", dsId, newDatasetName, instance).then((response) => {
                    let newDsId = response.data;
                    this.getDatasets(newDsId, true);
                    if (response.status === 'success') library.showDialog('The dataset was copied successfully.', false, false, { showAsAlert: true });
                    if (response.status === 'else') library.showDialog('There was a problem copying the dataset.', false, false, { showAsAlert: true });
                });
            };
            let datasetNameDialogTemplate = {
                name: 'copy-dataset-dialog-template',
                template: `<div>
                        <control name="datasetName" :prop-config="controls.datasetName" :value="datasetName" @update:value="datasetName = $event"></control>
                    </div>`,
                data() {
                    return {
                        datasetName: '',
                        controls: {
                            datasetName: {
                                name: 'datasetName',
                                label: 'Dataset Name',
                                type: 'string',
                                helpText: 'Enter the name for the new dataset.',
                                message: 'Optionally, enter a dataset name. NOTE: Dataset names cannot be changed once created.'
                            }
                        }
                    };
                },
                updated() {
                    newDatasetName = this.datasetName;
                },
                components: {
                    control: controlComponent
                }
            };
            return library.showDialog(datasetNameDialogTemplate, false, false, {
                title: 'Enter Dataset Name',
                name: 'datasetNameDialogTemplate',
                preventCloseOnBackdropClick: true
            }, funcPostSuccess);
        },
        async copyDatasetToDevDash(dsId) {
            await this.copyDataset(dsId, 'devDash');
        },
        async copyDatasetToDevDashQA(dsId) {
            await this.copyDataset(dsId, 'devDashQA');
        },
        async resetDataset(dsId) {
            await this.sDs.post("sds_resetDataset", dsId);
            this.getDatasets();
        },
        async deleteDataset(dsId) {
            await library.showDialog('Are you sure you want to remove dataset completely? This action can not be undone.',
                async () => {
                    await this.sDs.post("sds_deleteDataset", dsId);
                    this.getDatasets(false, true);
                },
                false,
                {
                    'title': 'Unsaved changes'
                });
        }
    }
};

componentStore.initComponent(datasetListCmp, datasetListConfig);
export { datasetListConfig, datasetListCmp };
export default datasetListCmp;
</script>

<style scoped>
    :deep(.sub-list-item-container) {
        background: #eee;
        display: flex;
        margin-bottom: 2px;
    }
    :deep(.dataset-header) {
        min-width: 100px;
        max-width: 100%;
        width: 100%;
    }
    :deep(.dataset-label) {
        flex-grow: 9;
        padding: 5px;
        overflow: hidden;
        font-weight: bold;
        cursor: pointer;
    }
    :deep(.dataset-label:hover)  {
        background: #f3d9dd;
    }
    :deep(.dataset-title) {
        font-size: 15px;
        white-space: nowrap;
    }
    :deep(.e-content > div) {
        margin:10px 0 10px 10px;
    }
    :deep(#DATASET_LIST_datasetList) {
        max-height: 500px;
        overflow-y: auto;
    }

    :deep(.sub-list-item-container.selected-dataset) {
        background: pink;
    }

    :deep(.button-area) {
        display: flex;
        margin-bottom: 15px;
    }

    :deep(.button-area div.new-control) {
        flex-grow: 9;
        margin-right: 10px;
    }

    :deep(.e-reload:before) {
        content: '\e99d' !important;
    }
    :deep(.action-area .control){
        height: 100%;
    }
    :deep(.dataset-status-flag) {
        margin-left: 10px;
        color: #e91a84;
        font-weight: bold;
    }

    :deep(.detail-indicator) {
        text-align: center;
        cursor: pointer;
        padding: 0 6px;
        min-width: 22px;
        height: 22px;
        line-height: 22px;
        background: rgba(255, 255, 255, .6);
        border-radius: 9px;
        display: block;
        float: right;
        margin-left: 5px;
        font-weight: normal;
    }

    :deep(.detail-indicator.dataset-lock) {
        width: 18px;
        padding: 2px 0 0 0;
    }
</style>
